import type { JSX } from 'react';
import { Fragment, useEffect, useState } from 'react';
import { Alert, Badge, Button, Center, Chip, Group, Loader, Stack, Switch, Table, Tabs, Text, TextInput, Title } from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { useInfiniteQuery } from '@tanstack/react-query';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { ApiError, systemLogApi, type LogLevel } from '../api';
import { LOG_LEVELS, formatDateTime } from '../format';
import { PageScroll, Sentinel } from '../ui/PageScroll';
import { TemplatesPanel } from './TemplatesPanel';
import { AiSettingsPanel } from './AiSettingsPanel';
import { ThemePanel } from './ThemePanel';
import { SamplePanel } from './SamplePanel';
import { ConnectionsPanel } from './ConnectionsPanel';
import { PortalJobsPanel } from './PortalJobsPanel';
import { linkTo } from '../ui/links';

/**
 * The tabs of Verwaltung › Einstellungen, in three groups: what PST is
 * connected to (mail, Destatis, Claude Code), what its documents and screens
 * look like, and help and diagnosis. The first tab is where the page opens.
 */
const SETTINGS_VIEWS = [
  { path: 'verbindungen', label: 'Verbindungen', description: 'Mailserver für Einladungen und Kennwort-Links, Zugang zu Destatis für den Baupreisindex, Schlüssel für die Claude-API, Zugänge zu Portalen, ntfy' },
  { path: 'abzuege', label: 'Abzüge', description: 'Abzüge aus ausschreiben.de, Baupreislexikon und heinze.de: Dienst am Server, Tageslimit, Aufträge' },
  { path: 'ki', label: 'KI-Auswertung', description: 'Weg (Claude Code am Server oder Claude-API), Verbindung, Modell und Aufwand' },
  { path: 'vorlagen', label: 'Vorlagen', description: 'Excel- und Word-Vorlagen des Büros, aus denen PST Dateien erzeugt' },
  { path: 'darstellung', label: 'Darstellung', description: 'Farben des Programms und der Excel-Dateien, Vorgabe: Corporate Design des Büros' },
  { path: 'beispiel', label: 'Beispieldaten', description: 'Beispielprojekt und Beispielangebot zum Ausprobieren, neu anlegen nach dem Löschen' },
  { path: 'protokoll', label: 'Systemprotokoll', description: 'Meldungen, Warnungen und Fehler der Anwendung' },
] as const;

/**
 * Verwaltung › Einstellungen: the administration's settings of PST itself,
 * one tab per area. Only for users.manage (routed in App).
 */
export function SettingsPage(): JSX.Element {
  const { tab } = useParams();
  const navigate = useNavigate();
  const current = SETTINGS_VIEWS.find((v) => v.path === tab);
  if (!current) return <Navigate to={`/einstellungen/${SETTINGS_VIEWS[0].path}`} replace />;
  return (
    <PageScroll>
      <Stack gap="sm" pb="xl">
        <Stack gap={0}>
          <Title order={3}>Einstellungen</Title>
          <Text size="sm" c="dimmed">Einstellungen und Zustand der Anwendung, nur für die Administration.</Text>
        </Stack>
        <Tabs value={current.path} keepMounted={false}>
          <Tabs.List style={{ flexWrap: 'nowrap', overflowX: 'auto' }}>
            {SETTINGS_VIEWS.map((v) => <Tabs.Tab key={v.path} value={v.path} title={v.description} component="a" {...linkTo(navigate, `/einstellungen/${v.path}`)}>{v.label}</Tabs.Tab>)}
          </Tabs.List>
          <Tabs.Panel value="protokoll" pt="sm"><SystemLog /></Tabs.Panel>
          <Tabs.Panel value="vorlagen" pt="sm"><TemplatesPanel /></Tabs.Panel>
          <Tabs.Panel value="ki" pt="sm"><AiSettingsPanel /></Tabs.Panel>
          <Tabs.Panel value="verbindungen" pt="sm"><ConnectionsPanel /></Tabs.Panel>
          <Tabs.Panel value="abzuege" pt="sm"><PortalJobsPanel /></Tabs.Panel>
          <Tabs.Panel value="darstellung" pt="sm"><ThemePanel /></Tabs.Panel>
          <Tabs.Panel value="beispiel" pt="sm"><SamplePanel /></Tabs.Panel>
        </Tabs>
      </Stack>
    </PageScroll>
  );
}

const LEVEL_ORDER: readonly LogLevel[] = ['error', 'warn', 'log', 'debug'];

/** The application's log, newest first, loaded page by page while scrolling. */
function SystemLog(): JSX.Element {
  const [levels, setLevels] = useState<LogLevel[]>(['error', 'warn', 'log']);
  const [query, setQuery] = useState('');
  const [q] = useDebouncedValue(query, 300);
  const [live, setLive] = useState(true);
  const [open, setOpen] = useState<number | null>(null);
  const log = useInfiniteQuery({
    queryKey: ['system-log', levels, q],
    queryFn: ({ pageParam }) => systemLogApi.page({ levels, q, before: pageParam }),
    initialPageParam: null as number | null,
    getNextPageParam: (last) => last.next,
    refetchInterval: live ? 15_000 : false,
  });
  useEffect(() => setOpen(null), [levels, q]);

  const lines = log.data?.pages.flatMap((p) => p.entries) ?? [];
  const first = log.data?.pages[0];

  return (
    <Stack gap="sm">
      <Text size="xs" c="dimmed">
        Was die Anwendung meldet: Starts, Einlesevorgänge, Warnungen, Fehler mit technischen Angaben. PST bewahrt die Zeilen {first?.retentionDays ?? 30} Tage auf. Zeile anklicken für die ganze Meldung.
      </Text>
      {first && (
        <Group gap="xs">
          <Text size="xs" c="dimmed">Letzte 24 Stunden:</Text>
          {LEVEL_ORDER.map((l) => <Badge key={l} size="sm" variant="light" color={LOG_LEVELS[l].color}>{first.lastDay[l]} {LOG_LEVELS[l].label}</Badge>)}
        </Group>
      )}
      <Group gap="sm" align="center">
        <Chip.Group multiple value={levels} onChange={(v) => setLevels(v as LogLevel[])}>
          <Group gap={6}>
            {LEVEL_ORDER.map((l) => <Chip key={l} value={l} size="xs" color={LOG_LEVELS[l].color}>{LOG_LEVELS[l].label}</Chip>)}
          </Group>
        </Chip.Group>
        <TextInput size="xs" placeholder="Suchen in Meldung und Bereich" value={query} onChange={(e) => setQuery(e.currentTarget.value)} style={{ flex: 1, minWidth: 180 }} />
        <Switch size="xs" label="laufend aktualisieren" checked={live} onChange={(e) => setLive(e.currentTarget.checked)} />
        <Button size="xs" variant="default" loading={log.isRefetching && !log.isFetchingNextPage} onClick={() => void log.refetch()}>Neu laden</Button>
      </Group>
      {log.isPending && <Center h={120}><Loader size="sm" /></Center>}
      {log.isError && <Alert color="red">{log.error instanceof ApiError ? log.error.message : 'Das Protokoll konnte nicht geladen werden.'}</Alert>}
      {log.data && lines.length === 0 && <Text size="sm" c="dimmed">Keine passenden Einträge.</Text>}
      {lines.length > 0 && (
        <Table.ScrollContainer minWidth={760}>
          <Table verticalSpacing={3} highlightOnHover className="pst-tabular">
            <Table.Thead><Table.Tr><Table.Th w={150}>Zeit</Table.Th><Table.Th w={90}>Stufe</Table.Th><Table.Th w={170}>Bereich</Table.Th><Table.Th>Meldung</Table.Th></Table.Tr></Table.Thead>
            <Table.Tbody>
              {lines.map((l) => (
                <Fragment key={l.id}>
                  <Table.Tr onClick={() => setOpen((x) => (x === l.id ? null : l.id))} style={{ cursor: 'pointer' }}>
                    <Table.Td><Text size="xs">{formatDateTime(l.at)}</Text></Table.Td>
                    <Table.Td><Badge size="sm" variant="light" color={LOG_LEVELS[l.level].color}>{LOG_LEVELS[l.level].label}</Badge></Table.Td>
                    <Table.Td><Text size="xs" ff="monospace" c="dimmed">{l.context ?? '—'}</Text></Table.Td>
                    <Table.Td><Text size="sm" lineClamp={open === l.id ? undefined : 2} style={{ wordBreak: 'break-word' }}>{l.message}</Text></Table.Td>
                  </Table.Tr>
                  {open === l.id && l.trace && (
                    <Table.Tr><Table.Td colSpan={4}><Text size="xs" ff="monospace" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{l.trace}</Text></Table.Td></Table.Tr>
                  )}
                </Fragment>
              ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      )}
      <Sentinel active={log.hasNextPage && !log.isFetchingNextPage} onVisible={() => void log.fetchNextPage()}>
        <Button size="xs" variant="subtle" onClick={() => void log.fetchNextPage()}>Ältere Einträge laden</Button>
      </Sentinel>
      {log.isFetchingNextPage && <Center h={40}><Loader size="xs" /></Center>}
    </Stack>
  );
}
